import { useCallback, useEffect, useRef, useState } from "react";
import { Gift } from "lucide-react";
import api from "../lib/api";
import { SecondaryAction } from "./kit";
import LoadFailureNote from "./LoadFailureNote";
import SettingsMoreCard from "./SettingsMoreCard";
import ReferralQr from "./ReferralQr";
import { OUTLINE_ACTION_STYLE } from "./SettingsSignOut";
import { friendsLine, joinLink } from "../lib/referralLink";

/**
 * Settings › Invite a friend (#1571, D417), new look only (the shipped Settings
 * page never draws it, D217). A card in More, placed after Billing: the QR code
 * for `<origin>/join/<code>`, the link, Share and "N friends have joined".
 *
 * Share is the Web Share API where the browser has it, and copies the link
 * where it does not (or where the share sheet fails). Cancelling the share
 * sheet is not a failure and says nothing.
 *
 * REWARDS ARE DORMANT until the server says otherwise (`rewards_active`): the
 * link, QR and count all work, and one muted line says "Rewards start soon."
 * The card never promises the free month while it is not on.
 *
 * Not drawn when the server says this account has no code (403), the same way
 * Billing is not drawn when billing is off.
 */
const small = { fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink-muted)", margin: 0 };

export const REWARDS_SOON = "Rewards start soon.";
export const REWARDS_LINE = "A free month each: theirs at checkout, yours after they pay for their second month.";

export default function InviteFriendCard() {
  const [load, setLoad] = useState("pending"); // pending | ready | failed | hidden
  const [me, setMe] = useState(null);
  const [note, setNote] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const read = useCallback(async () => {
    setLoad("pending");
    try {
      const { data } = await api.get("/referrals/me");
      if (!mounted.current) return;
      setMe(data);
      setLoad("ready");
    } catch (e) {
      if (!mounted.current) return;
      setLoad(e?.response?.status === 403 ? "hidden" : "failed");
    }
  }, []);

  useEffect(() => {
    read();
  }, [read]);

  if (load === "hidden") return null;

  const link = me?.code ? joinLink(window.location.origin, me.code) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setNote("Link copied.");
    } catch {
      setNote("Copy the link above.");
    }
  };

  const share = async () => {
    setNote("");
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: "CricketCompass", text: "Join me on CricketCompass.", url: link });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    await copy();
  };

  return (
    <SettingsMoreCard
      icon={Gift}
      title="Invite a friend"
      subtitle="Share your link or let a friend scan the code."
      data-testid="settings-invite-friend"
    >
      {load === "failed" && <LoadFailureNote what="your invite link" onRetry={read} testId="invite-friend-failed" tapBox />}
      {load === "ready" && me && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ alignSelf: "flex-start", maxWidth: "100%" }}>
            <ReferralQr value={link} />
          </div>
          <p data-testid="invite-friend-link" style={{ ...small, color: "var(--nw-ink)", wordBreak: "break-all", userSelect: "all" }}>
            {link}
          </p>
          <SecondaryAction onClick={share} data-testid="invite-friend-share" style={OUTLINE_ACTION_STYLE}>
            Share
          </SecondaryAction>
          <p role="status" aria-live="polite" data-testid="invite-friend-note" style={{ ...small, minHeight: note ? undefined : 0 }}>
            {note}
          </p>
          <p data-testid="invite-friend-count" style={{ ...small, color: "var(--nw-ink)", fontWeight: 600 }}>
            {friendsLine(me.joined ?? 0)}
          </p>
          <p data-testid="invite-friend-rewards" style={small}>
            {me.rewards_active ? REWARDS_LINE : REWARDS_SOON}
          </p>
        </div>
      )}
    </SettingsMoreCard>
  );
}
